import type { RouteRecordRaw } from "vue-router";
import { createRouter, createWebHistory } from "vue-router";
import login from "@/router/route/login";
import impersonateLoginRoute from "@/router/route/impersonate-login";
import idpLoginRoute from "@/router/route/idp-login";
import dashboard from "@/router/route/dashboard";
import errors from "@/router/route/errors";
import redirect from "@/router/route/redirect";
import type { MjfRouteMeta } from "@shared/types/localTypes/router";
import { RouteNames } from "@shared/types/localTypes/router";
import {
  AllSubscriptionsSelectLabel,
  useSubscriptionsStore,
} from "@shared/stores/subscriptions";
import { navigationService } from "@shared/services/navigation";
import { useResponsiveStore } from "@shared/stores/responsive";
import landing from "./route/landing";
import marketplace from "./route/marketplace";
import { useUpgradeStore } from "@shared/stores/upgrade";
import { useRoutingStore } from "@shared/stores/routing";
import { useBetaChecker } from "@/use/useBetaChecker";
import logger from "@shared/utils/logger.ts";
import { maintenanceStatus } from "@shared/services/maintenanceStatus.ts";
import { useSessionLogic } from "@shared/use/useSessionLogic.ts";
import { appService } from "@shared/services/app.ts";
import { useActionPagesStore } from "@shared/stores/actionPages.ts";
import { analyticsService } from "@shared/services/analytics.ts";
import { useKeycloakStore } from "@shared/stores/keycloak.ts";
import window from "@shared/utils/app-window.ts";
import { isRunningOnTour } from "@shared/utils/tour.ts";

const routes: Array<RouteRecordRaw> = [
  login,
  impersonateLoginRoute,
  idpLoginRoute,
  dashboard,
  errors,
  landing,
  marketplace,
  ...redirect,
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  linkActiveClass: "my-jfrog-active-link",
});

router.onError((error) => {
  if (
    error &&
    error.message &&
    (error.message.includes("Failed to fetch dynamically imported module") ||
      error.message.includes("Importing a module script failed"))
  ) {
    logger.error(`Force Browser Reload on: ${error.message}`);
    window.location.reload();
  }
});

router.beforeEach(async (to) => {
  const upgradeStore = useUpgradeStore();
  const routingStore = useRoutingStore();
  const actionPagesStore = useActionPagesStore();
  const targetRouteMetas: MjfRouteMeta = to.meta;

  maintenanceStatus.checkIsMyJfUnderMaintenance();

  const keycloakStore = useKeycloakStore();
  if (!isRunningOnTour()) {
    await keycloakStore.initializeKeycloak();
  }

  // Called from jfrog.com, should always send the user to the Keycloak login page.
  // In case the session expired (on another tab), the app would still think the session is active,
  // therefore we need to check the Keycloak auth state.
  if (
    !isRunningOnTour() &&
    to.name === RouteNames.Login &&
    "websiteLogin" in to.query &&
    !keycloakStore.keycloak?.authenticated
  ) {
    // The extra scope lets us monitor logins originating from this client
    // (keycloak prepends "openid" automatically).
    await keycloakStore.login({ scope: "website_mjf_login" });
    return false;
  }

  if (upgradeStore.upgradeViewIsVisible) {
    upgradeStore.dismissUpgradeView();
    routingStore.setCurrentRouteAsCancelled();
    return false;
  }

  if (actionPagesStore.currentActionPage && !actionPagesStore.isAllowed) {
    actionPagesStore.clearCurrentActionPage();
    routingStore.setCurrentRouteAsCancelled();
    return false;
  }

  const isCurrentSessionActive = useSessionLogic().isSessionActive();

  if (isCurrentSessionActive && targetRouteMetas.autoLogout) {
    await appService.logOut();
    return;
  }

  if (isCurrentSessionActive && targetRouteMetas.isGuest) {
    return { name: RouteNames.AllSubscriptions };
  }

  const allowDisplayInMobile = (to.meta as MjfRouteMeta).allowDisplayInMobile;
  useResponsiveStore().setSkipMobileMaskDisclaimer(
    allowDisplayInMobile || false,
  );
});

router.afterEach(async (to, from) => {
  const routingStore = useRoutingStore();

  if (routingStore.currentRouteIsCancelled) {
    routingStore.clearCurrentRouteCancelled();
    return false;
  }

  document.title = "MyJFrog - " + (to.name as string);

  const subscriptionsStore = useSubscriptionsStore();
  const currentSubscriptionNumber =
    subscriptionsStore.currentSubscriptionNumber;
  const targetSubscriptionNumber = to.params.subscriptionId;

  navigationService.manageMenuItemsOnSubscriptionChanged(
    targetSubscriptionNumber as string,
  );

  if (currentSubscriptionNumber !== targetSubscriptionNumber) {
    const computedSubscriptionNumber =
      targetSubscriptionNumber || AllSubscriptionsSelectLabel;
    await subscriptionsStore.setCurrentSubscription(
      computedSubscriptionNumber as string,
    );

    await useBetaChecker().disableBetaIfNeeded();
  }

  const toName = typeof to.name === "string" ? to.name : "unknown";
  const fromName = typeof from.name === "string" ? from.name : "unknown";

  if (toName !== fromName) {
    analyticsService.track("page_view", {
      page_name: toName,
      prev_page: fromName,
    });
  }
});

export default router;
